/**
 * serviceCardPrice.ts
 *
 * Single source of truth for the price block rendered on a marketplace
 * service CARD (the grid tile — not the detail page).
 *
 * Why this exists
 * ---------------
 * The card grid was rendered by two independent components with two
 * independent price formatters:
 *
 *   - `ServiceGridSSR`        — pre-hydration / crawler-visible HTML,
 *                               whole dollars, en-dash range ("$65 – $130/mo")
 *   - `SimplePricingDisplay`  — hydrated client render,
 *                               two decimals, hyphen range ("$65.00 - 65.00/mo")
 *
 * They disagreed on decimal precision and on range handling, so the price
 * visibly changed on hydration and the two surfaces could contradict each
 * other. Both now call `getServiceCardPrice()`, so parity is structural
 * rather than maintained by hand.
 *
 * The card contract
 * -----------------
 * The headline is a FIXED SHAPE — `[From ]<amount><unit>` — and nothing
 * else. Duration, escalation, and one-time fees never enter the headline;
 * they render in the slots below it. This keeps the price number in the
 * same position on every tile so a buyer can scan a column of cards.
 *
 *   headline   From $65.00/mo
 *   schedule   Then $90.00 (Months 13–24) · $100.00 (Month 25+)
 *   fees       Setup: $119.00
 *
 * Two rules worth preserving:
 *
 * 1. "From " is DERIVED, never typed. It appears only when there is real
 *    variation above the displayed number (multiple variants at different
 *    prices, or a min/max span). A "From " on a flat price teaches buyers
 *    to distrust every other number on the page.
 *
 * 2. A price that changes over time MUST surface its schedule. A card that
 *    advertises $65 while the customer is billed $100 by month 25 is the
 *    single most trust-damaging thing a marketplace can ship.
 */

import { findVariant, getVariantList, usesVariantPricing } from './variantHelpers';

type AnyRecord = Record<string, any>;

/** One period in a time-based price schedule, e.g. `{ label: 'Months 13–24', recurring_cents: 9000 }`. */
export interface PriceScheduleSegment {
  label: string;
  recurring_cents: number;
}

export interface ServiceCardPrice {
  /** Fixed-shape price headline. Empty string when no price is known. */
  headline: string;
  /** Escalation disclosure. Null when the price does not change over time. */
  schedule: string | null;
  /** One-time charges (setup / shipping / other). */
  fees: string[];
}

const EMPTY: ServiceCardPrice = { headline: '', schedule: null, fees: [] };

/** Cents → `$1,234.56`. Two decimals everywhere; unit pricing is often sub-dollar. */
export function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function suffixForFrequency(frequency: unknown): string {
  switch (frequency) {
    case 'monthly':
      return '/mo';
    case 'quarterly':
      return '/quarter';
    case 'annually':
      return '/year';
    case 'one_time':
      return ' one time charge';
    default:
      return '';
  }
}

function toFiniteNumber(value: unknown): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Build the escalation line from a variant's `pricing_schedule`.
 *
 * Segment 0 is the period the headline already shows, so it is omitted —
 * repeating it would just push the numbers that matter off the card. Labels
 * are rendered verbatim (they are authored per-supplier, e.g. "Months 13–24",
 * "Month 25+") rather than parsed, so this stays correct for any number of
 * periods and any labelling convention.
 *
 * Returns null when there is no schedule, only one period, or every period
 * carries the same amount — i.e. when there is nothing to disclose.
 */
export function formatPriceSchedule(
  segments: unknown,
): string | null {
  if (!Array.isArray(segments) || segments.length < 2) return null;

  const valid = segments.filter(
    (s: AnyRecord) =>
      s &&
      typeof s === 'object' &&
      typeof s.label === 'string' &&
      s.label.trim() !== '' &&
      toFiniteNumber(s.recurring_cents) != null,
  ) as PriceScheduleSegment[];

  if (valid.length < 2) return null;

  // No escalation to disclose if every period costs the same.
  const amounts = new Set(valid.map((s) => Number(s.recurring_cents)));
  if (amounts.size < 2) return null;

  const later = valid
    .slice(1)
    .map((s) => `${formatMoney(Number(s.recurring_cents))} (${s.label.trim()})`);

  return `Then ${later.join(' · ')}`;
}

/** Read the time-based schedule off the variant the card is quoting. */
function getScheduleForVariant(variant: AnyRecord | undefined): string | null {
  if (!variant) return null;
  return formatPriceSchedule((variant as AnyRecord).pricing_schedule);
}

function collectFees(pricing: AnyRecord | null | undefined): string[] {
  if (!pricing) return [];
  const fees: string[] = [];
  const nrc = toFiniteNumber(pricing.nrc_cents);
  const shipping = toFiniteNumber(pricing.shipping_cents);
  const other = toFiniteNumber(pricing.other_cents);
  // `nrc_cents` is the total non-recurring charge — for Spectrum coax that is
  // install + activation already combined. Do not split it here; the detail
  // page owns the breakdown.
  if (nrc != null && nrc > 0) fees.push(`Setup: ${formatMoney(nrc)}`);
  if (shipping != null && shipping > 0) fees.push(`Shipping: ${formatMoney(shipping)}`);
  if (other != null && other > 0) fees.push(`Other: ${formatMoney(other)}`);
  return fees;
}

/**
 * True when the service's variants are priced differently from one another,
 * meaning the displayed number is a floor the buyer will resolve upward.
 */
function variantsDifferInPrice(service: AnyRecord): boolean {
  if (!usesVariantPricing(service)) return false;
  const list = getVariantList(service);
  if (list.length <= 1) return false;
  const prices = list
    .map((v: AnyRecord) => toFiniteNumber(v?.pricing?.recurring_cents ?? v?.amount_cents))
    .filter((n): n is number => n != null);
  return new Set(prices).size > 1;
}

/**
 * Span fallback for services that expose only denormalized min/max columns.
 * Returns the FLOOR plus whether a real span exists — the card shows a single
 * anchor ("From $65.00/mo"), never a range, so the max is used as a signal
 * rather than rendered.
 */
function getSpanFallback(
  service: AnyRecord,
): { cents: number; spans: boolean } | null {
  const min =
    toFiniteNumber(service.price_min_cents) ?? toFiniteNumber(service.price_range_min);
  const max =
    toFiniteNumber(service.price_max_cents) ?? toFiniteNumber(service.price_range_max);

  if (min != null && min > 0) {
    return { cents: min, spans: max != null && max > min };
  }

  const base = toFiniteNumber(service.base_price) ?? toFiniteNumber(service.price_cents);
  if (base != null && base > 0) {
    return { cents: base, spans: false };
  }

  return null;
}

/**
 * Compute the full card price block for a service.
 *
 * Safe against every malformed shape the marketplace has produced so far:
 * missing `variants.variants`, flat shape-2 variants (`amount_cents` instead
 * of a nested `pricing` object), absent `default_pricing`, and string-typed
 * cents columns. Never throws; returns an empty block when no price is known.
 */
export function getServiceCardPrice(
  service: AnyRecord | null | undefined,
): ServiceCardPrice {
  if (!service || typeof service !== 'object') return EMPTY;

  // ---- Per-unit rail -------------------------------------------------
  // Services that scale with a natural unit (lines, seats, users, sites)
  // quote the unit rate. "From" is always correct here: the buyer supplies
  // the quantity, so the total is genuinely above the number shown.
  const unitBasis = service.tile_unit_basis as string | undefined;
  const unitCents = toFiniteNumber(service.tile_price_cents);
  const unitLabel = service.tile_unit_label as string | null | undefined;
  if (unitBasis && unitBasis !== 'flat' && unitCents != null && unitLabel) {
    return {
      headline: `From ${formatMoney(unitCents)} ${unitLabel}`,
      schedule: null,
      fees: [],
    };
  }

  // ---- Variant pricing -----------------------------------------------
  if (usesVariantPricing(service)) {
    const variant = findVariant(service, service.default_variant_key);
    if (variant) {
      // Variants ship in two shapes:
      //   1. { key, pricing: { recurring_cents, frequency, ... } }
      //   2. { key, frequency, amount_cents }
      // Never assume `.pricing` exists.
      let pricing: AnyRecord | undefined = variant.pricing;

      const terms: number[] = Array.isArray(service.contract_term_months)
        ? service.contract_term_months
        : [];
      const defaultTerm = service.default_term_months ?? terms[0] ?? null;
      if (defaultTerm && Array.isArray(variant.term_overrides)) {
        const override = variant.term_overrides.find(
          (o: AnyRecord) => o?.term_months === defaultTerm,
        );
        if (override) pricing = override;
      }

      if (!pricing) {
        pricing = {
          recurring_cents: toFiniteNumber(variant.amount_cents) ?? 0,
          frequency: variant.frequency,
        };
      }

      const suffix = suffixForFrequency(pricing.frequency);
      const fees = collectFees(pricing);
      const schedule = getScheduleForVariant(variant);

      const min = toFiniteNumber(pricing.min_recurring_cents);
      const max = toFiniteNumber(pricing.max_recurring_cents);
      const recurring = toFiniteNumber(pricing.recurring_cents);

      // A min/max pair renders as a single anchor, not "X - Y". When
      // min === max there is no span at all and the old code emitted the
      // nonsense "$65.00 - 65.00/mo".
      if (min != null && min > 0) {
        const spans = max != null && max > min;
        return {
          headline: `${spans ? 'From ' : ''}${formatMoney(min)}${suffix}`,
          schedule,
          fees,
        };
      }

      if (recurring != null && recurring > 0) {
        const prefix = variantsDifferInPrice(service) ? 'From ' : '';
        return { headline: `${prefix}${formatMoney(recurring)}${suffix}`, schedule, fees };
      }

      const span = getSpanFallback(service);
      if (span) {
        return {
          headline: `${span.spans ? 'From ' : ''}${formatMoney(span.cents)}/mo`,
          schedule,
          fees,
        };
      }
      return { headline: '', schedule, fees };
    }
  }

  // ---- Multi-frequency -----------------------------------------------
  if (
    service.pricing_model === 'multi_frequency' &&
    Array.isArray(service.frequencies) &&
    service.frequencies.length > 0
  ) {
    const freq = service.frequencies[0];
    const cents = toFiniteNumber(freq?.recurring_cents);
    if (cents != null && cents > 0) {
      return {
        headline: `${formatMoney(cents)}${suffixForFrequency(freq?.frequency)}`,
        schedule: null,
        fees: collectFees(freq),
      };
    }
  }

  // ---- Default pricing -----------------------------------------------
  const dp = service.default_pricing as AnyRecord | undefined;
  if (dp) {
    const suffix = suffixForFrequency(dp.frequency);
    const fees = collectFees(dp);
    const min = toFiniteNumber(dp.min_recurring_cents);
    const max = toFiniteNumber(dp.max_recurring_cents);
    const recurring = toFiniteNumber(dp.recurring_cents);

    if (min != null && min > 0) {
      const spans = max != null && max > min;
      return {
        headline: `${spans ? 'From ' : ''}${formatMoney(min)}${suffix}`,
        schedule: null,
        fees,
      };
    }

    if (recurring != null && recurring > 0) {
      return { headline: `${formatMoney(recurring)}${suffix}`, schedule: null, fees };
    }

    const span = getSpanFallback(service);
    if (span) {
      return {
        headline: `${span.spans ? 'From ' : ''}${formatMoney(span.cents)}/mo`,
        schedule: null,
        fees,
      };
    }
    return { headline: '', schedule: null, fees };
  }

  // ---- Denormalized columns only -------------------------------------
  const span = getSpanFallback(service);
  if (span) {
    return {
      headline: `${span.spans ? 'From ' : ''}${formatMoney(span.cents)}/mo`,
      schedule: null,
      fees: [],
    };
  }

  return EMPTY;
}
